import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Checkbox,
} from "~/components/ui";
import type { EntityRelationLifecycle } from "~/features/entities/types";
import { MAX_CASCADE_RELATION_IDS } from "~/features/entities/types";

interface EntityActivationDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** When true the action will deactivate; otherwise activate. */
  deactivate: boolean;
  processing: boolean;
  relations: EntityRelationLifecycle;
  relationsLoading: boolean;
  /** The preview could not be read. Confirming would act on an unknown set of relations. */
  relationsFailed: boolean;
  /** Relation ids ticked on the activate dialog. Ignored while deactivating. */
  selectedRelationIds: string[];
  onToggleRelation: (relationId: string, next: boolean) => void;
  onConfirm: () => void;
}

export function EntityActivationDialog({
  open,
  onOpenChange,
  deactivate,
  processing,
  relations,
  relationsLoading,
  relationsFailed,
  selectedRelationIds,
  onToggleRelation,
  onConfirm,
}: EntityActivationDialogProps) {
  const { t } = useTranslation();

  // Deactivating takes the entity's live relations with it, so the list is a preview and has
  // nothing to choose. Activating brings back only what the operator ticks, so it does.
  const listed = deactivate ? relations.toDeactivate : relations.activatable;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {deactivate ? t("feature.entities.detail.deactivate") : t("feature.entities.detail.activate")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {deactivate ? t("feature.entities.detail.confirmDeactivate") : t("feature.entities.detail.confirmActivate")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {relationsFailed ? (
          <p className="text-[13px] text-danger">{t("feature.entities.detail.relationsLoadFailed")}</p>
        ) : relationsLoading ? (
          <p className="text-[13px] text-fg-3">{t("feature.entities.detail.relationsLoading")}</p>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {listed.length === 0 ? (
              // Nothing to say on a deactivate with no live relations; on an activate the
              // absence is worth stating, since the operator may be looking for one.
              deactivate ? null : (
                <p className="text-[13px] text-fg-3">{t("feature.entities.detail.noRelationsToActivate")}</p>
              )
            ) : (
              <>
                <p className="mb-2 text-[13px] text-fg-3">
                  {deactivate
                    ? t("feature.entities.detail.relationsToDeactivate")
                    : t("feature.entities.detail.relationsToActivate")}
                </p>
                {deactivate || listed.length <= MAX_CASCADE_RELATION_IDS ? null : (
                  <p className="mb-2 text-[13px] text-fg-3">
                    {t("feature.entities.detail.relationsSelectionCapped", { count: MAX_CASCADE_RELATION_IDS })}
                  </p>
                )}
                <ul className="space-y-1">
                  {listed.map((relation) => (
                    <li key={relation.relationId} className="flex items-center gap-2 text-[13px]">
                      {deactivate ? null : (
                        <Checkbox
                          checked={selectedRelationIds.includes(relation.relationId)}
                          onCheckedChange={(next) => onToggleRelation(relation.relationId, next === true)}
                          disabled={
                            processing ||
                            (!selectedRelationIds.includes(relation.relationId) &&
                              selectedRelationIds.length >= MAX_CASCADE_RELATION_IDS)
                          }
                          aria-label={relation.displayName}
                        />
                      )}
                      <Link to={`/relations/${relation.relationId}`} className="truncate underline">
                        {relation.displayName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={processing}>{t("ui.cancel")}</AlertDialogCancel>
          {/* Confirming while the preview is missing would act on an unknown set of relations:
              on a deactivate the operator would not have seen what goes down with the entity,
              on an activate the request would carry no selection at all. */}
          <AlertDialogAction onClick={onConfirm} disabled={processing || relationsLoading || relationsFailed}>
            {t("ui.ok")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
